import { useState, useRef, useLayoutEffect, useCallback, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { PanelRightOpen, PanelRightClose } from 'lucide-react';
import { Sheet, SheetContent } from '../ui/Sheet';
import { Separator } from '../ui/Separator';
import { CommentThread } from './CommentThread';
import { CommentModal } from './CommentModal';
import { ClaimDetailTab } from './ClaimDetailTab';
import { DrawerHeader } from './DrawerHeader';
import { MemberDetailsTab } from './MemberDetailsTab';
import { ReferralInfoPanel } from './ReferralInfoPanel';
import { ProviderClaimsTab } from './ProviderClaimsTab';
import { fetchReferralDetail } from '../../services/referralService';
import { cn } from '../../lib/utils';

// Case-insensitive check — the legacy column casing is inconsistent in real
// data. Centralised so all visibility predicates stay in lockstep.
const isProviderReferral = (referral) =>
  (referral?.referral_type || '').toLowerCase() === 'provider';

const isClaimReferral = (referral) =>
  (referral?.referral_type || '').toLowerCase() === 'claim';

const isMemberReferral = (referral) =>
  (referral?.referral_type || '').toLowerCase() === 'member';

// Tabs declare their own visibility via an isVisible predicate over the
// (referral, userRole) context. Keeps the filter in one place and makes the
// claim-only constraint a first-class property of the tab definition rather
// than a special-case boolean. Tabs without a predicate are always visible.
const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'claim_detail', label: 'Claim Detail View', isVisible: ({ referral }) => isClaimReferral(referral) },
  { key: 'provider_claims', label: 'Provider Claims', isVisible: ({ referral }) => isProviderReferral(referral) },
  { key: 'member_details', label: 'Member Details', isVisible: ({ referral }) => isMemberReferral(referral) },
];

function ReferralDrawer({ open, onClose, referral, userRole, readOnly = false, onActivityPosted }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [underlineStyle, setUnderlineStyle] = useState({ left: 0, width: 0 });
  const [isReferralCollapsed, setIsReferralCollapsed] = useState(false);
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  // Bumping this re-runs CommentThread's fetch effect to refresh after a post.
  const [commentsReloadKey, setCommentsReloadKey] = useState(0);
  // Drives the modal's Status-option disable rule. Defaults to ACKNOWLEDGE
  // until the first activities GET resolves.
  const [currentWorkflowStep, setCurrentWorkflowStep] = useState('ACKNOWLEDGE');
  // Distinct steps the referral has been in; drives the header timeline.
  const [visitedWorkflowSteps, setVisitedWorkflowSteps] = useState(['ACKNOWLEDGE']);
  // Latest status-bearing activity drives the modal: its leaf reason_code
  // filters the Reason dropdown when the user opens it without picking a new
  // Status, and its display fields populate the "Current Status / Current
  // Reason" labels at the top of the modal. Null/undefined for brand-new
  // referrals — the modal falls back to "—".
  const [latestStatus, setLatestStatus] = useState({
    reasonId: null,
    statusDisplay: null,
    reasonDisplay: null,
  });
  // Marks the next handleActivitiesLoaded as post-submit so we can patch the
  // parent list with the freshly-loaded snapshot. Cleared once the patch fires.
  const pendingPatchRef = useRef(false);
  // Keep referral_id in a ref so handleActivitiesLoaded can read the current
  // value without referral_id being a useCallback dep — that dep would cause
  // the callback to be recreated on every referral change, which triggers
  // CommentThread to re-fire fetchActivities and overwrite latestStatus.
  const referralIdRef = useRef(null);
  const tabsRef = useRef(new Map());

  // Detail payload is fetched once at the drawer level and passed to whichever
  // tab needs it (Overview's ReferralInfoPanel, Member Details). Lifting the
  // fetch up means the network call doesn't fire again every time the user
  // toggles between tabs that consume the same endpoint.
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  useEffect(() => {
    if (!open || !referral?.referral_id) {
      setDetailData(null);
      setDetailLoading(false);
      setDetailError(null);
      return undefined;
    }

    let cancelled = false;
    setDetailLoading(true);
    setDetailError(null);
    setDetailData(null);

    fetchReferralDetail(referral.referral_id)
      .then((result) => {
        if (!cancelled) setDetailData(result);
      })
      .catch((err) => {
        if (!cancelled) setDetailError(err);
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });

    return () => { cancelled = true; };
  }, [open, referral?.referral_id]);

  const handleClose = useCallback(() => {
    if (onClose) onClose();
  }, [onClose]);

  const visibleTabs = useMemo(
    () => TABS.filter((t) =>
      typeof t.isVisible === 'function' ? t.isVisible({ referral, userRole }) : true,
    ),
    [referral, userRole],
  );

  // If the active tab disappears (e.g. referral switched from Claim → Member
  // mid-drawer and "Claim Detail" is no longer in visibleTabs), fall back to
  // the first visible tab so we don't render an empty content area.
  useEffect(() => {
    if (!visibleTabs.some((t) => t.key === activeTab) && visibleTabs.length > 0) {
      setActiveTab(visibleTabs[0].key);
    }
  }, [visibleTabs, activeTab]);

  // Guard against the render that happens *before* the activeTab-reset effect
  // fires. Without this, switching from a Claim referral with "Claim Detail"
  // active to a Provider referral would render <ClaimDetailTab refId={B.id}>
  // for one render pass — firing /claim-details/ for a Provider referral and
  // hitting a 404 — before the effect resets activeTab. Resolving activeTab
  // through visibleTabs eliminates that window.
  const renderedTab = visibleTabs.some((t) => t.key === activeTab)
    ? activeTab
    : visibleTabs[0]?.key;

  const updateUnderline = (key) => {
    const el = tabsRef.current.get(key);
    if (el) {
      setUnderlineStyle({ left: el.offsetLeft, width: el.offsetWidth });
    }
  };

  useLayoutEffect(() => {
    if (open) {
      updateUnderline(activeTab);
    }
  }, [open, activeTab]);

  const handleTabClick = (key) => {
    setActiveTab(key);
    updateUnderline(key);
  };

  // Keep referralIdRef current on every render.
  referralIdRef.current = referral?.referral_id ?? null;

  // CommentThread forwards what came down with the activity list response.
  // Memoised so CommentThread's effect deps don't churn on every render.
  const handleActivitiesLoaded = useCallback((activities, workflowStep, visited, latestStatusAct) => {
    if (workflowStep) setCurrentWorkflowStep(workflowStep);
    if (Array.isArray(visited) && visited.length > 0) {
      setVisitedWorkflowSteps(visited);
    }
    // latestStatusAct is the absolute most recent status-bearing activity
    // (get_latest_status_activity), which includes migrate_comments
    // snapshots hidden from the visible thread. Scanning `activities`
    // would miss those for migrated referrals.
    //
    // `status_display` is the level above the leaf (universal for non-HCSC,
    // HCSC primary for HCSC). Matches the list's Status column.
    const list = activities ?? [];
    setLatestStatus({
      reasonId: latestStatusAct ? latestStatusAct.reason_code : null,
      statusDisplay: latestStatusAct ? latestStatusAct.status_display : null,
      reasonDisplay: latestStatusAct ? latestStatusAct.reason_display : null,
    });

    // If this load was triggered by a post (modal onSuccess bumped reloadKey
    // and set the pending flag), patch the parent list row with the freshly-
    // loaded snapshot. Backend already serializes display + recipient name +
    // stripped comment on each activity, so the snapshot needs no transform.
    if (pendingPatchRef.current) {
      pendingPatchRef.current = false;
      const currentReferralId = referralIdRef.current;
      if (onActivityPosted && currentReferralId) {
        const latestCommentAct = list.find((a) => a.comment) ?? null;
        const patch = {};
        if (latestStatusAct) {
          patch.tpa_carrier_status = latestStatusAct.status_display;
          patch.tpa_carrier_reason = latestStatusAct.reason_display;
          patch.assigned_to = latestStatusAct.recipient_organization_name ?? null;
          patch.assigned_action = latestStatusAct.assigned_action ?? null;
          patch.due_date = latestStatusAct.due_date ?? null;
        }
        if (latestCommentAct) {
          patch.latest_comment = latestCommentAct.comment;
        }
        if (Object.keys(patch).length > 0) {
          onActivityPosted(currentReferralId, patch);
        }
      }
    }
  }, [onActivityPosted]);

  return (
    <>
      <Sheet open={open} onOpenChange={handleClose}>
        <SheetContent onClose={handleClose}>
          <DrawerHeader
            referral={referral}
            readOnly={readOnly}
            currentWorkflowStep={currentWorkflowStep}
            visitedWorkflowSteps={visitedWorkflowSteps}
            liveStatus={latestStatus.statusDisplay}
            liveReason={latestStatus.reasonDisplay}
          />

          <div className="px-6 bg-drawer-bg relative mt-2">
            <div className="relative flex gap-6 -mb-px">
              {visibleTabs.map((tab) => (
                <button
                  key={tab.key}
                  ref={(el) => { if (el) tabsRef.current.set(tab.key, el); }}
                  onClick={() => handleTabClick(tab.key)}
                  className={cn(
                    'px-1 py-3 text-sm font-medium transition-colors',
                    activeTab === tab.key ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {tab.label}
                </button>
              ))}
              <motion.div
                className="absolute bottom-0 h-[2.5px] rounded-full bg-primary"
                animate={{ left: underlineStyle.left, width: underlineStyle.width }}
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              />
            </div>
            <Separator className="mt-0" />
          </div>

          {/* readOnly gates the Overview composer (+) and DrawerHeader flag buttons. */}
          <div className="flex-1 flex flex-col min-h-0">
            {renderedTab === 'overview' && (
              <div className="flex-1 flex flex-col gap-3 px-6 py-5 bg-background min-h-0">
                <div className={cn('grid min-h-0 flex-1 gap-4', isReferralCollapsed ? 'grid-cols-1' : 'grid-cols-2')}>
                  {/* Comments - left column */}
                  <div className="flex flex-col min-h-0">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {!readOnly && (
                          <button
                            onClick={() => setCommentModalOpen(true)}
                            className="w-7 h-7 rounded border border-border flex items-center justify-center text-foreground/80 hover:bg-accent transition-colors text-lg leading-none"
                          >
                            +
                          </button>
                        )}
                        <span className="text-sm font-semibold text-foreground">{readOnly ? 'Comments' : 'Comment & Update Status'}</span>
                      </div>
                      <button
                        data-testid="collapse-toggle"
                        onClick={() => setIsReferralCollapsed((v) => !v)}
                        className="w-7 h-7 rounded border border-border flex items-center justify-center text-foreground/80 hover:bg-accent transition-colors"
                        title={isReferralCollapsed ? 'Show referral info' : 'Hide referral info'}
                      >
                        {isReferralCollapsed
                          ? <PanelRightOpen className="h-4 w-4" />
                          : <PanelRightClose className="h-4 w-4" />
                        }
                      </button>
                    </div>
                    <div data-testid="comment-thread" className="flex flex-col min-h-0 flex-1">
                      <CommentThread
                        refId={referral?.referral_id}
                        readOnly={readOnly}
                        reloadKey={commentsReloadKey}
                        onActivitiesLoaded={handleActivitiesLoaded}
                      />
                    </div>
                  </div>

                  {/* Referral Info Panel - right column */}
                  {!isReferralCollapsed && (
                    <div data-testid="referral-info-panel" className="flex flex-col min-h-0 h-full">
                      <ReferralInfoPanel
                        caseId={referral?.referral_id}
                        referralType={referral?.referral_type}
                        referralDate={referral?.publish_dt}
                        providerName={referral?.referral_name}
                        data={detailData}
                        loading={detailLoading}
                        error={detailError}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
            {renderedTab === 'claim_detail' && (
              <ClaimDetailTab refId={referral?.referral_id} />
            )}
            {renderedTab === 'provider_claims' && (
              <ProviderClaimsTab refId={referral?.referral_id} />
            )}
            {renderedTab === 'member_details' && (
              <MemberDetailsTab
                identifiers={detailData?.identifiers}
                phiMaskedFields={detailData?.phi_masked_fields}
                loading={detailLoading}
                error={detailError}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
      {commentModalOpen && (
        <CommentModal
          open={commentModalOpen}
          onClose={() => setCommentModalOpen(false)}
          onSuccess={() => {
            // Mark the next activities reload as post-submit. When it
            // resolves, handleActivitiesLoaded builds a snapshot from the
            // freshly-loaded data and fires onActivityPosted(refId, patch)
            // so the parent list can patch the row in place — no full
            // refetch, no spinner.
            pendingPatchRef.current = true;
            setCommentsReloadKey((k) => k + 1);
          }}
          refId={referral?.referral_id}
          referralTpaId={referral?.tpa_id ?? null}
          latestStatusReasonId={latestStatus.reasonId}
          currentStatus={latestStatus.statusDisplay}
          currentReason={latestStatus.reasonDisplay}
        />
      )}
    </>
  );
}

export { ReferralDrawer };
